import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { ConfirmModal } from 'common/components';

import CollapsibleThumbnailsContainer from './CollapsibleThumbnailsContainer';
import DroppableThumbnail from './DroppableThumbnail';
import { ThumbnailImage } from './types';
import { getNextPossibleUploadIndex } from './utils';
import { ITEM_MAX_IMAGE_COUNT } from '../../constants';

type Props = {
  className?: string;
  isExpanded?: boolean;
  imagesAlt: string;
  images: ThumbnailImage[];
  canDelete: boolean;
  changingImages?: number[];
  onDrop?: (files: File[], index: number) => void;
  onDelete?: (deleteAt: number) => Promise<void>;
};

const CollapsibleThumbnails = ({
  className,
  isExpanded,
  images,
  imagesAlt,
  canDelete,
  changingImages = [],
  onDrop,
  onDelete,
}: Props) => {
  const [activeThumbnailIndex, setActiveThumbnailIndex] = useState<number | null>(null);
  const [deletingThumbnailIndex, setDeletingThumbnailIndex] = useState<number | null>(null);

  const thumbnailPlaceholders = useMemo(() => {
    const thumbnails = [];

    for (let index = 0; index < ITEM_MAX_IMAGE_COUNT; index++) {
      thumbnails.push({
        index,
        url: images[index]?.url ?? null,
        blurhash: images[index]?.blurhash ?? null,
      });
    }

    return thumbnails.reverse(); // ThumbnailsContainer shows items in reverse order to adjust proper z-index
  }, [images]);

  const onThumbnailDragStateChange = useCallback(
    (isDragActive: boolean, index: number) => {
      if (isDragActive) {
        setActiveThumbnailIndex(index);
      } else if (activeThumbnailIndex === index) {
        setActiveThumbnailIndex(null);
      }
    },
    [activeThumbnailIndex, setActiveThumbnailIndex],
  );

  const dragActiveIndex = getNextPossibleUploadIndex(activeThumbnailIndex ?? images.length, images.length);

  const closeDeleteThumbnailModal = useCallback(() => {
    setDeletingThumbnailIndex(null);
  }, []);

  const handleThumbnailDelete = useCallback(() => {
    if (deletingThumbnailIndex === null) {
      return;
    }

    if (onDelete) {
      toast.promise(onDelete(deletingThumbnailIndex), {
        loading: <FormattedMessage id="shared-menu.image-delete.deleting" />,
        success: <FormattedMessage id="shared-menu.image-delete.success" />,
        error: <FormattedMessage id="shared-menu.image-delete.error" />,
      });
    }

    setDeletingThumbnailIndex(null);
  }, [deletingThumbnailIndex, onDelete]);

  return (
    <div onClick={e => e.stopPropagation()}>
      <CollapsibleThumbnailsContainer
        className={className}
        isExpanded={isExpanded || changingImages.length > 0}
        totalImagesIndicator={images.length}
      >
        {thumbnailPlaceholders.map(({ url, blurhash, index }) => (
          <DroppableThumbnail
            key={index}
            blurhash={blurhash}
            src={url}
            index={index}
            isDragActive={changingImages.length <= 0 && isExpanded && dragActiveIndex === index}
            onDragStateChange={onThumbnailDragStateChange}
            isUploading={changingImages.includes(index)}
            alt={imagesAlt}
            onDrop={onDrop}
            onDelete={setDeletingThumbnailIndex}
            canDelete={canDelete}
          />
        ))}
      </CollapsibleThumbnailsContainer>
      <ConfirmModal
        header={<FormattedMessage id="shared-menu.image-delete.delete-image" />}
        description={<FormattedMessage id="shared-menu.image-delete.delete-image-description" />}
        cancelText={<FormattedMessage id="state.cancel" />}
        confirmText={<FormattedMessage id="shared-menu.image-delete.confirm" />}
        onCancel={closeDeleteThumbnailModal}
        onConfirm={handleThumbnailDelete}
        open={deletingThumbnailIndex !== null}
      />
    </div>
  );
};

export default CollapsibleThumbnails;
